import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	ICreateJobThunkArgs,
	IDeleteJobThunkArgs,
	IGetJobsThunkArgs,
	IGetJobThunkArgs,
	IJob,
} from 'src/features/job/domain/types';
import { jobsApi } from 'src/features/job/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const JOB_LOADING_KEY = 'job';

export const createJob = createAsyncThunk<
	IJob,
	ICreateJobThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'job/createDownloadJob',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await jobsApi.createJob(request, siteSlug);
			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create job ');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getJobs = createAsyncThunk<
	IPaginatedResponse<IJob>,
	IGetJobsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'job/getJobs',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await jobsApi.getJobs(request, siteSlug);
			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to get jobs ');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getJob = createAsyncThunk<
	IJob,
	IGetJobThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'job/getJob',
	async (
		{ jobId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await jobsApi.getJob(jobId, siteSlug);
			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to get job');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteJob = createAsyncThunk<
	void,
	IDeleteJobThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'job/deleteJob',
	async (
		{ jobId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await jobsApi.deleteJob(jobId, siteSlug);
			onSuccess?.();
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to delete job');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: JOB_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
